import { useEffect, useRef, useState } from "react";
import { Bell, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import {
	useNotificationList,
	useMarkNotificationRead,
	useClearNotifications,
	useDeleteNotification,
	type Notification,
} from "../../../hooks/useNotifications";
import styles from "./NotificationBell.module.css";

function notificationLink(n: Notification): string | null {
	const data = n.data;
	if (n.type === "company_assigned" && typeof data?.["companyId"] === "string") {
		return `/companies/${data["companyId"]}`;
	}
	if (n.type === "company_assigned" && typeof data?.["ownerId"] === "string") {
		return `/companies?salesPerson=${data["ownerId"]}`;
	}
	if (n.type === "contract_reminder" && typeof data?.["contractId"] === "string") {
		return `/contracts/${data["contractId"]}`;
	}
	if (n.type === "activity_reminder" && typeof data?.["activityId"] === "string") {
		return `/activities/${data["activityId"]}`;
	}
	if (n.type === "special_day") {
		return "/special-days";
	}
	return null;
}

export function NotificationBell() {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const { data } = useNotificationList();
	const markRead = useMarkNotificationRead();
	const clearAll = useClearNotifications();
	const deleteOne = useDeleteNotification();

	useEffect(() => {
		if (!open) return;
		const onClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", onClickOutside);
		return () => document.removeEventListener("mousedown", onClickOutside);
	}, [open]);

	const items = data?.items ?? [];
	const unreadCount = data?.unreadCount ?? 0;

	const handleItemClick = (n: Notification) => {
		if (!n.isRead) markRead.mutate(n.id);
		setOpen(false);
	};

	return (
		<div className={styles.container} ref={containerRef}>
			<button
				type="button"
				className={styles.bellButton}
				onClick={() => setOpen((o) => !o)}
				aria-label={t("notifications")}
			>
				<Bell size={18} />
				{unreadCount > 0 && (
					<span className={styles.badge}>{unreadCount > 99 ? "99+" : unreadCount}</span>
				)}
			</button>
			{open && (
				<div className={styles.panel}>
					<div className={styles.panelHeader}>
						{t("notifications")}
						{items.length > 0 && (
							<button
								type="button"
								className={styles.clearAllButton}
								onClick={() => clearAll.mutate()}
								disabled={clearAll.isPending}
								aria-label={t("clearAllNotifications")}
								title={t("clearAllNotifications")}
							>
								<X size={16} />
							</button>
						)}
					</div>
					<div className={styles.panelList}>
						{items.length === 0 && <div className={styles.empty}>{t("noNotifications")}</div>}
						{items.map((n) => {
							const link = notificationLink(n);
							const className = [styles.item, !n.isRead && styles.unread].filter(Boolean).join(" ");
							const content = (
								<>
									<div className={styles.itemTitle}>{n.title}</div>
									{n.body && <div className={styles.itemBody}>{n.body}</div>}
									<div className={styles.itemTime}>
										{new Date(n.createdAt).toLocaleDateString()}
									</div>
								</>
							);
							return (
								<div key={n.id} className={styles.itemRow}>
									{link ? (
										<Link to={link as never} className={className} onClick={() => handleItemClick(n)}>
											{content}
										</Link>
									) : (
										<button type="button" className={className} onClick={() => handleItemClick(n)}>
											{content}
										</button>
									)}
									<button
										type="button"
										className={styles.itemDeleteButton}
										onClick={(e) => {
											e.stopPropagation();
											e.preventDefault();
											deleteOne.mutate(n.id);
										}}
										aria-label={t("delete")}
										title={t("delete")}
									>
										<X size={14} />
									</button>
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
